import { useDispatch } from 'react-redux';
import { useEffect, useState } from 'react';
import { Box, Tab, Tabs } from '@mui/material';

import { Header } from '@app/sections/header';
import { Strings } from '@app/constants';
import { useTable } from '@app/components/table';
import CategoryList from './category-list';
import { useDialog, useRequestState } from '@app/hooks';
import AddCategory from './add-category-form';
import { getCategory } from '../slice';
import {
  selectAddCategoryState,
  selectAddParentCategoryState,
  selectCategoryState,
} from '../selectors';
import { usePageCount } from '@app/hooks/use-page-count';

type CategoryTab = 'parent' | 'child';

const TABLE_HEAD_CSV = [
  { key: 'id', label: 'id', align: 'left' },
  { key: 'name', label: 'Child Category', align: 'left' },
  { key: 'parentCategory', label: 'Parent Category', align: 'left' },
  { key: 'type', label: 'type', align: 'left' },
  { key: 'topCategory', label: 'Top Category', align: 'left' },
  { key: 'products', label: 'Products', align: 'left', sortable: false },
  { key: 'active', label: 'Status', align: 'left', sortable: false },
];

const CategoryListPage = () => {
  const tableProps = useTable();
  const categoryDialog = useDialog();
  const parentCategoryDialog = useDialog();
  const dispatch = useDispatch();
  const { page, rowsPerPage, onChangePage } = tableProps;
  const { count } = usePageCount(getCategory);
  const [query, setQuery] = useState('');
  const [tab, setTab] = useState<CategoryTab>('child');

  useEffect(() => {
    const base = { page, limit: rowsPerPage, kind: tab, admin: true as const };
    if (query.length !== 0) {
      dispatch(getCategory({ ...base, q: query }));
    } else {
      dispatch(getCategory(base));
    }
  }, [dispatch, page, query, rowsPerPage, tab]);

  const refetchList = () => {
    if (typeof onChangePage === 'function') {
      onChangePage(null as any, 0);
    }
    dispatch(
      getCategory({
        page: 1,
        limit: rowsPerPage,
        kind: tab,
        admin: true,
        ...(query.length ? { q: query } : {}),
      })
    );
  };

  useRequestState({
    stateSelector: selectAddCategoryState,
    onSuccess: () => {
      // Child create → show Children tab populated
      if (tab !== 'child') {
        setTab('child');
      } else {
        refetchList();
      }
    },
  });

  useRequestState({
    stateSelector: selectAddParentCategoryState,
    onSuccess: () => {
      if (tab !== 'parent') {
        setTab('parent');
      } else {
        refetchList();
      }
    },
  });

  const { data, loading } = useRequestState({
    stateSelector: selectCategoryState,
    errorShown: true,
  });

  const handleSearchChange = (event: any) => {
    setQuery(event.target.value);
  };

  const handleTabChange = (_: React.SyntheticEvent, next: CategoryTab) => {
    setTab(next);
    setQuery('');
    // Reset to first page when switching tabs
    if (typeof onChangePage === 'function') {
      onChangePage(null as any, 0);
    }
  };

  const flattenAry = (data: any) => {
    const value = data?.map((elem: any) => {
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      return `${elem?.name}:${elem?.description}`;
    });
    return value.join('; ');
  };

  const flattenData = (data: any) => {
    const serializeData = data?.map((elem: any) => {
      return {
        ...elem,
        products: flattenAry(elem.products),
        category: elem.name,
        parentCategory: elem.parentCategoryName,
        type: elem.type,
        topCategory: elem.topCategory === true ? 'Active' : 'InActive',
        active: elem.active === true ? 'Active' : 'Inactive',
      };
    });
    return serializeData;
  };

  return (
    <>
      <Header
        title={Strings.pageTitle.categories}
        actionButtonTitle={Strings.button.addNewCategory}
        searchBarProps={{
          placeholder: tab === 'parent' ? 'Search parent categories…' : 'Search child categories…',
          onChange: handleSearchChange,
        }}
        onClickActionButton={categoryDialog.show}
        additionalButtonTitle={Strings.button.addParentCategory}
        onClickAdditionalButton={parentCategoryDialog.show}
        data={flattenData(data)}
        csvHead={TABLE_HEAD_CSV}
        csvFileName={'category-list.csv'}
        additionalExportButtonTitle={Strings.button.exportCsv}
        additionalExportPdfButtonTitle={Strings.button.exportpdf}
        orientation="landscape"
      />

      <Box sx={{ px: 2, pt: 1, borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tab} onChange={handleTabChange} aria-label="category tabs">
          <Tab label="Children" value="child" />
          <Tab label="Parents" value="parent" />
        </Tabs>
      </Box>

      <CategoryList
        loading={loading}
        tableProps={tableProps}
        rows={data}
        count={count}
        page={page}
        kind={tab}
      />
      {categoryDialog.visible && <AddCategory open onClose={categoryDialog.hide} />}
      {parentCategoryDialog.visible && (
        <AddCategory
          open
          onClose={parentCategoryDialog.hide}
          title={Strings.button.addParentCategory}
          isParentCategory
        />
      )}
    </>
  );
};

export { CategoryListPage };
